import React, { useState } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Button, useTheme, Tabs, Tab, Card, CardContent, CardActions, Chip, Divider, IconButton, Tooltip, Avatar
} from '@mui/material';
import {
    Workspaces as WorkflowsIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon, History as PendingIcon,
    AccessTime as TimeIcon, Person as PersonIcon, AttachMoney as MoneyIcon, Assignment as RequestIcon
} from '@mui/icons-material';

// --- MOCK Data ---
const mockApprovals = [
    { id: 101, type: 'Expense Claim', status: 'Pending', requester: 'Alice Johnson', amount: 350.50, submitted: '2 hours ago', priority: 'High', details: 'Team dinner reimbursement.' },
    { id: 102, type: 'Leave Request', status: 'Pending', requester: 'Ben Smith', days: 5, submitted: '1 day ago', priority: 'Medium', details: 'Vacation leave from 10/20 to 10/24.' },
    { id: 103, type: 'Software Access', status: 'Pending', requester: 'Chloe Davis', software: 'JIRA Pro', submitted: '3 days ago', priority: 'High', details: 'Requires immediate access for project launch.' },
    { id: 104, type: 'New Hire Approval', status: 'Pending', requester: 'David Lee', role: 'Senior Dev', submitted: '1 week ago', priority: 'Low', details: 'Final offer letter approval.' },
    { id: 105, type: 'Budget Transfer', status: 'Approved', requester: 'Eve White', amount: 15000, submitted: '2 days ago', priority: 'Medium', details: 'Transfer to Q4 marketing budget.' },
    { id: 106, type: 'Equipment Purchase', status: 'Rejected', requester: 'Frank Green', item: 'Laptop Upgrade', submitted: '3 days ago', priority: 'Low', details: 'Rejected due to cost cap policy.' },
];

const approvalCategories = [
    { label: 'Pending Approvals', icon: PendingIcon, key: 'Pending', color: 'warning' },
    { label: 'Approved History', icon: ApproveIcon, key: 'Approved', color: 'success' },
    { label: 'Rejected History', icon: RejectIcon, key: 'Rejected', color: 'error' },
];

// --- Sub-Component: Approval Card ---
const ApprovalCard = ({ approval, theme, handleApprove, handleReject }) => {
    
    const getIcon = (type) => {
        switch (type) {
            case 'Expense Claim': return <MoneyIcon color="success" />;
            case 'Leave Request': return <TimeIcon color="primary" />;
            case 'New Hire Approval': return <PersonIcon color="info" />;
            default: return <RequestIcon color="action" />;
        }
    };
    
    return (
        <Card 
            elevation={8} 
            sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                borderLeft: `6px solid ${approval.priority === 'High' ? theme.palette.error.main : theme.palette.warning.main}`,
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: theme.shadows[15],
                }
            }}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box display="flex" alignItems="center">
                        {getIcon(approval.type)}
                        <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, color: theme.palette.primary.dark }}>
                            {approval.type}
                        </Typography>
                    </Box>
                    <Chip 
                        label={approval.priority}
                        size="small"
                        color={approval.priority === 'High' ? 'error' : 'warning'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                </Box>
                
                <Typography variant="h6" component="div" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                    {approval.details.length > 50 ? approval.details.substring(0, 50) + '...' : approval.details}
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Box display="flex" alignItems="center" mb={1}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.secondary.main, mr: 1 }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" color="text.primary" fontWeight={600}>
                        {approval.requester}
                    </Typography>
                </Box>
                
                <Typography variant="caption" color="text.secondary" display="block">
                    **Submitted:** {approval.submitted}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    **ID:** {approval.id}
                </Typography>

            </CardContent>
            
            {/* Action Buttons (Only visible for Pending status) */}
            {approval.status === 'Pending' && (
                <CardActions sx={{ p: 2, bgcolor: theme.palette.grey[50], justifyContent: 'flex-end', borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button 
                        size="small" 
                        startIcon={<RejectIcon />} 
                        onClick={() => handleReject(approval.id)}
                        color="error"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    >
                        Reject
                    </Button>
                    <Button 
                        size="small" 
                        startIcon={<ApproveIcon />} 
                        onClick={() => handleApprove(approval.id)}
                        color="success"
                        variant="contained"
                        sx={{ ml: 1, fontWeight: 700 }}
                    >
                        Approve
                    </Button>
                </CardActions>
            )}
            
            {/* Status Chip for History */}
            {approval.status !== 'Pending' && (
                    <Box sx={{ p: 2, bgcolor: theme.palette.grey[50], borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Chip
                            label={approval.status}
                            color={approval.status === 'Approved' ? 'success' : 'error'}
                            icon={approval.status === 'Approved' ? <ApproveIcon sx={{ fontSize: 18 }} /> : <RejectIcon sx={{ fontSize: 18 }} />}
                            sx={{ fontWeight: 700, width: '100%' }}
                        />
                    </Box>
            )}
        </Card>
    );
};

// --- Main Workflow Approval Component ---
const WorkflowApproval = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState('Pending');
    const [approvals, setApprovals] = useState(mockApprovals);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleApprove = (id) => {
        console.log(`Approval ID ${id} Approved.`);
        // In a real app: API call, then update state
        setApprovals(prev => prev.map(a => a.id === id ? { ...a, status: 'Approved' } : a));
    };

    const handleReject = (id) => {
        console.log(`Approval ID ${id} Rejected.`);
        // In a real app: API call, then update state
        setApprovals(prev => prev.map(a => a.id === id ? { ...a, status: 'Rejected' } : a));
    };

    const filteredApprovals = approvals.filter(p => p.status === activeTab);

    // --- RENDER COMPONENT ---
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box 
                sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    // Animation: Slide-down header
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    animation: 'headerSlide 0.8s ease-out forwards',
                    '@keyframes headerSlide': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    color="primary.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    {/* FIX: Replaced undefined WorkspacesIcon with the imported alias WorkflowsIcon */}
                    <WorkflowsIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    Workflow & Pending Approvals
                </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<RequestIcon />} 
                    sx={{ fontWeight: 600 }}
                >
                    View All Workflows
                </Button>
            </Box>

            {/* Tabs for Filtering Status */}
            <Paper elevation={4} sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    indicatorColor="primary" 
                    textColor="primary" 
                    variant="fullWidth" 
                    aria-label="approval status tabs"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    {approvalCategories.map(category => (
                        <Tab 
                            key={category.key} 
                            label={category.label} 
                            value={category.key} 
                            icon={<category.icon />} 
                            iconPosition="start"
                            sx={{ fontWeight: 700, minWidth: 150, color: theme.palette[category.color].main }}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Approval List Grid */}
            <Grid 
                container 
                spacing={4}
                // Animation: Content grid fade-in
                sx={{ 
                    opacity: 0,
                    animation: 'contentFadeIn 1s ease-out 0.4s forwards',
                    '@keyframes contentFadeIn': { 'to': { opacity: 1 } }
                }}
            >
                {filteredApprovals.length > 0 ? (
                    filteredApprovals.map((approval) => (
                        <Grid item xs={12} sm={6} lg={4} key={approval.id}>
                            <ApprovalCard 
                                approval={approval} 
                                theme={theme} 
                                handleApprove={handleApprove} 
                                handleReject={handleReject} 
                            />
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `2px dashed ${theme.palette.grey[300]}` }}>
                            <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
                                All clear!
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                No **{activeTab.toLowerCase()}** approvals found at this time.
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
};

export default WorkflowApproval;